import { FC, useEffect, useState } from 'react';

import {
  Alert,
  Chip,
  CircularProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import axios from '../../../sharedModules/utils/axios.utils';
import { PreviousReceipt, PreviousReceiptsResponse, SERVMAN_LOOKUP_TIMEOUT_MS } from '../model';

interface PreviousReceiptsTableProps {
  poNumber: string;
  location: string;
}

const countLabel = (count: number, singular: string, plural: string): string =>
  `${count} ${count === 1 ? singular : plural}`;

// N is the POs in this response; M is the uncut total, which is larger than the rows when truncated.
const headerTag = (result: PreviousReceiptsResponse): string => {
  const poCount = new Set(result.receipts.map((receipt) => receipt.po_no)).size;
  const summary = `${countLabel(poCount, 'PO', 'POs')} · ${countLabel(result.total, 'receipt', 'receipts')}`;
  if (result.order_numbers.length === 0) return summary;
  return `Order ${result.order_numbers.join(', ')} · ${summary}`;
};

const cell = (value: string | number | null): string => (value == null || value === '' ? '' : `${value}`);

const PreviousReceiptsTable: FC<PreviousReceiptsTableProps> = ({ poNumber, location }) => {
  const [result, setResult] = useState<PreviousReceiptsResponse>();
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    if (!poNumber) {
      setLoading(false);
      setResult(undefined);
      setErrorMessage('');
      return undefined;
    }

    let stale = false;

    const load = async () => {
      setLoading(true);
      setErrorMessage('');

      try {
        const response = await axios.get<PreviousReceiptsResponse>(
          `/warehouse/receiving/purchase_orders/${poNumber}/previous_receipts`,
          { params: { location }, timeout: SERVMAN_LOOKUP_TIMEOUT_MS },
        );
        if (stale) return;
        setResult(response.data);
      } catch (error: any) {
        if (stale) return;
        setResult(undefined);
        setErrorMessage(error?.response?.data?.error || 'Something went wrong loading previous receipts.');
      } finally {
        if (!stale) setLoading(false);
      }
    };

    load();

    return () => {
      stale = true;
    };
  }, [poNumber, location]);

  const showEmpty = !loading && !errorMessage && !!result && result.receipts.length === 0;
  const showTable = !loading && !errorMessage && !!result && result.receipts.length > 0;

  return (
    <Stack data-testid="previous-receipts" spacing={1} style={{ marginBottom: 24 }}>
      <Typography variant="h6">Previously received locations</Typography>
      {result && (
        <Typography variant="body2" color="text.secondary">
          {headerTag(result)}
        </Typography>
      )}
      {loading && <CircularProgress size={20} />}
      {errorMessage && <Alert severity="error">{errorMessage}</Alert>}
      {showEmpty && <Typography>No earlier receipts for this PO or its order.</Typography>}
      {showTable && result && (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>PO</TableCell>
                <TableCell>Vendor</TableCell>
                <TableCell>Warehouse</TableCell>
                <TableCell>Rack</TableCell>
                <TableCell>Shelf</TableCell>
                <TableCell>Bin</TableCell>
                <TableCell>Qty</TableCell>
                <TableCell>Received</TableCell>
                <TableCell>Received by</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {result.receipts.map((receipt: PreviousReceipt, index) => (
                <TableRow key={`${receipt.po_no}-${receipt.received_at}-${index}`}>
                  <TableCell>
                    {cell(receipt.po_no)}
                    {receipt.this_po && <Chip label="this PO" size="small" sx={{ ml: 1 }} />}
                  </TableCell>
                  <TableCell>{cell(receipt.vendor_name)}</TableCell>
                  <TableCell>{cell(receipt.warehouse_name)}</TableCell>
                  <TableCell>{cell(receipt.rack)}</TableCell>
                  <TableCell>{cell(receipt.shelf)}</TableCell>
                  <TableCell>{cell(receipt.bin)}</TableCell>
                  <TableCell>{cell(Number(receipt.quantity))}</TableCell>
                  <TableCell>{cell(receipt.received_at)}</TableCell>
                  <TableCell>{cell(receipt.received_by)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      {result?.truncated && <Typography>Showing 200 of {result.total} receipts.</Typography>}
    </Stack>
  );
};

export default PreviousReceiptsTable;
